<script lang="ts" setup>
import {
  callOnce,
  computed,
  useCreators,
  useDialog,
  useMaps,
  usePlaces,
  useSpots,
  watch,
} from "#imports";
import MPImg from "../widgets/MPImg.vue";
import MPBookmark from "../widgets/MPBookmark.vue";
import MPButton from "../forms/MPButton.vue";
import MPTag from "../widgets/MPTag.vue";
import MPShare from "../dialogs/MPShare.vue";

const props = defineProps<{
  spotId: string;
  showTier?: boolean;
  showDestination?: boolean;
  showCreator?: boolean;
  to?: string;
  version?: "large" | "small";
  actions?: boolean;
}>();

const emits = defineEmits<{
  updated: [];
}>();

const { openDialog } = useDialog();
const spotStore = useSpots();
const mapStore = useMaps();
const creatorStore = useCreators();
const placeStore = usePlaces();

const spot = computed(() => {
  const spot = spotStore.getById(props.spotId).value!;
  const map = mapStore.getById(spot?.map_id).value!;
  const creator = creatorStore.getById(map?.user_id).value;
  const place = placeStore.getById(spot.place_id).value;

  return {
    ...spot,
    map,
    creator,
    place,
  };
});

const loadData = async () => {
  const spot = await spotStore.fetchById(props.spotId);
  const map = await mapStore.fetchById(spot.value!.map_id);
  await creatorStore.fetchById(map.value!.user_id);
  if (spot.value!.place_id) {
    await placeStore.fetchById(spot.value!.place_id);
  }
};

// we have to make the key unique to the component because otherwise it won't run more than once
await callOnce(`MPSpotCard-${props.spotId}`, loadData, { mode: "navigation" });
watch(() => props.spotId, loadData);
</script>

<template>
  <div :class="['spot-card', version]">
    <NuxtLinkLocale
      :to="
        to ??
        `/maps/${spot.map.current_slug}/spots/${spot.current_slug}#no-scroll`
      "
    >
      <div class="image">
        <MPImg
          :src="spot.cover_image_url ?? '/media/images/placeholder.webp'"
          :width="version === 'small' ? 160 : 500"
          :height="version === 'small' ? 160 : 500"
          forceResize
        />
        <div v-if="props.showCreator" class="spot-creator-image">
          <MPImg
            :src="
              spot.creator?.profile_image_url ??
              '/media/images/placeholder-user.webp'
            "
            :width="100"
            :height="100"
          />
        </div>
      </div>
    </NuxtLinkLocale>
    <div class="actions-container" v-if="actions">
      <div class="actions">
        <MPBookmark
          size="small"
          version="secondary"
          link
          bookmark="spot_bookmarks"
          tooltip
          :hideLabel="true"
          backgroundColor="var(--color-slate-100)"
          iconColor="var(--color-rose-500)"
          @completed="emits('updated')"
          :id="spot.id"
        />
        <MPButton
          icon="ri-share-line"
          version="secondary"
          size="small"
          rounded
          link
          hideLabel
          backgroundColor="var(--color-slate-100)"
          @click="
            openDialog(MPShare, {
              id: spot.id,
              version: 'spot',
            })
          "
        />
      </div>
    </div>
    <NuxtLinkLocale
      class="content-wrapper"
      :to="
        to ??
        `/maps/${spot.map.current_slug}/spots/${spot.current_slug}#no-scroll`
      "
    >
      <div class="content">
        <div>
          <div v-if="showDestination && spot.place" class="city typeface-small">
            {{ spot.place?.fullName }}
          </div>
          <span class="name typeface-summary" v-if="spot.name">
            {{ spot.name }}
          </span>
          <div class="summary typeface-regular" v-if="spot.summary">
            {{ spot.summary }}
          </div>
          <div
            class="selected-by"
            v-if="spot.creator?.mappr_handle && props.showCreator"
          >
            By<span> @{{ spot.creator?.mappr_handle }}</span>
          </div>
        </div>
        <div class="info">
          <MPTag v-if="spot.categories" class="category" size="small" rounded>
            <i :class="spot.categories?.icon" />
            {{
              spot.categories.name?.length > 8
                ? spot.categories.name?.substring(0, 8) + "&hellip;"
                : spot.categories.name
            }}
          </MPTag>
          <MPTag
            v-if="spot?.visibility && spot?.visibility !== 'public' && showTier"
            :class="['visibility', spot?.visibility]"
            size="small"
            rounded
          >
            {{
              spot?.visibility === "supporter"
                ? "supporter"
                : spot?.visibility === "subscriber"
                  ? "premium"
                  : spot?.visibility === "authenticated"
                    ? "members"
                    : ""
            }}&nbsp;Only
          </MPTag>
        </div>
      </div>
    </NuxtLinkLocale>
  </div>
</template>

<style lang="postcss" scoped>
.spot-card {
  display: flex;
  flex-direction: column;
  /* min-width: 18rem; */

  max-width: 22.125rem;
  min-width: 10rem;
  width: 100%;
  height: 100%;
  border-radius: 1rem;
  overflow: hidden;
  position: relative;
}

.spot-card.small {
  min-width: 100%;
  max-width: auto;
  display: grid;
  grid-template-columns: 40% 60%;
}

.image {
  width: 100%;
  height: 9em;
  > img {
    min-height: 9em;
    min-width: 100%;
    border-radius: 1rem;
    object-fit: cover;
  }
}

.small .image {
  border-radius: 0.5rem;
  overflow: hidden;
  height: 100%;
}

.spot-creator-image {
  position: absolute;
  bottom: -1.25rem;
  left: 22px;
  display: flex;

  img {
    object-fit: cover;
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 100%;
  }
}

.small .spot-creator {
  gap: 0.5rem;
  img {
    height: 2.25rem;
    width: 2.25rem;
  }
  span {
    font-size: 0.8rem;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.actions-container {
  position: relative;

  .actions {
    position: absolute;
    display: flex;
    gap: 0.75rem;
    top: -1.25rem;
    right: 0.75rem;
    button {
      border: 1px solid var(--color-slate-200) !important;
      border-radius: 50%;
    }
  }
  .actions:deep(button) {
    border: 1px solid var(--color-slate-200) !important;
    border-radius: 50%;
  }
}

.content-wrapper {
  height: 100%;
}

.small {
  position: relative;
}

.small .actions-container {
  position: absolute;
  right: -0.5rem;
  bottom: -1rem;
  .actions {
    position: relative;
    display: flex;
    gap: 0.5rem;
  }
}

.small .actions:deep(button),
.small .actions:deep(i) {
  background: transparent !important;
  padding: 0 !important;
  /* border: none !important; */
}

.content {
  max-width: 100%;
  height: 100%;
  background-color: white;
  padding: 1rem 22px;
  display: flex;
  flex-direction: column;
  justify-items: start;
  gap: auto;
  color: var(--color-slate-800);
  justify-content: space-between;
  > * {
    width: fit-content;
  }
}

.small .content {
  padding: 0 0.75rem 0.25rem;
  background-color: transparent;
  /* justify-content: flex-start; */
}

.city {
  margin-top: 0.75rem;
  margin-bottom: 0.25rem;
  color: var(--color-rose-500);
  font-size: 12px;
  font-weight: 500;
}

.category {
  font-size: 16px;
  position: absolute;
  top: 22px;
  left: 22px;
  background-color: white;
  padding: 0.5rem 0.75rem;
  color: var(--color-slate-800);
  i {
    color: var(--color-slate-800);
  }
}

.spot-card.small .category {
  top: 12px;
  left: 12px;
  padding: 0.15rem 0.5rem;
}

.summary {
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.45rem;
  color: var(--color-slate-500);
}

.selected-by {
  margin-top: 1rem;
  span {
    color: var(--color-rose-400);
  }
}

.name {
  color: var(--color-slate-900);
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.small .name {
  line-height: 1.35;
}

.visibility {
  text-transform: capitalize;
  position: absolute;
  font-size: 16px;
  top: 22px;
  right: 22px;
  padding: 0.5rem 0.75rem;
}

.visibility.authenticated {
  background-color: var(--color-rose-500);
  color: var(--color-rose-50);
}

.visibility.supporter {
  background-color: var(--color-green-500);
  color: var(--color-green-50);
}

.visibility.subscriber {
  background-color: var(--color-indigo-500);
  color: var(--color-indigo-50);
}

.spot-card.small .visibility {
  position: relative;
  top: 5px;
  right: 5px;
  padding: 0.15rem 0.5rem;
}

.spot-card.small .visibility.authenticated {
  background-color: transparent;
  color: var(--color-rose-500);
}

.spot-card.small .visibility.supporter {
  background-color: transparent;
  color: var(--color-green-500);
}

.spot-card.small .visibility.subscriber {
  background-color: transparent;
  color: var(--color-indigo-500);
}

.info {
  margin: 0.75rem 0 0;
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  white-space: no-wrap;
}

.small .info {
  margin: 0 0 0.5rem;
}

.small .summary {
  line-height: 1.35rem;
  margin-bottom: 0.5rem;
}

.small .name {
  margin: 0.25rem 0;
}
</style>
